import type { Building } from "building-types/building/buildingTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import { BuildingGenerationIssueType } from "building-types/settings/buildingGenerationIssue";
import type { GeneratedStairBlock, GeneratedStairSystem, StairBlock } from "building-types/stairs/stairTypes";
import type { RWTable, Table } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { generateStairSystem, addPosts, aggregateWallTrimFacesFromBlocks } from "../elements/stairSystem/stairSystems";
import { resolveStairBlock, resolveStairSystem } from "../elements/stairSystem/stairUtil";
import { addStairSystemComponentInstances } from "../generationTools/components/componentStairSystems";
import { shouldGenerateEntity } from "../utils/entityIsolation";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  levels,
  mirror,
  mirrorTranslationX,
  doc: { optionSets, instanceOptionSets, stairSystems, stairBlocks, settings: docSettings },
  settings,
  optionChoices: buildingOptionChoices,
  rooms,
  roomFloorAssemblies,
  resolvedDatums,
  doc,
  entityDisabledByGroup,
  foundationPatternEdgeBehaviors,
  idToParents,
  relatedStairSystemsMap,
  enabledFeatures,
  isolatedEntity,
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    openings: doc.openings,
    levels,
    stairSystems,
    stairBlocks,
    mirror,
    mirrorTranslationX,
    stairSystemOptionChoices: resolveOptionsInFormula(stairSystems, definitions, buildingOptionChoices),
    stairBlocksOptionChoices: resolveOptionsInFormula(stairBlocks, definitions, buildingOptionChoices),
    rawOptionChoices: buildingOptionChoices,
    definitions,
    rooms,
    resolvedDatums,
    settings,
    docSettings: docSettings,
    stairSettings: settings.stair,
    stairCatalog: doc.stairCatalog,
    columns: settings.column,
    roomFloorAssemblies,
    entityDisabledByGroup,
    foundationPatternEdgeBehaviors,
    idToParents,
    relatedStairSystemsMap: relatedStairSystemsMap ?? {},
    enabledFeatures,
    isolatedEntity,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  stairBlocksOptionChoices: "shallow",
  stairSystemOptionChoices: "shallow",
  rawOptionChoices: "shallow",
  definitions: "shallow",
  foundationPatternEdgeBehaviors: "shallow",
  idToParents: "shallow",
  relatedStairSystemsMap: "shallow",
  enabledFeatures: "shallow",
  isolatedEntity: "shallow",
});

function generate({
  levels,
  stairSystems,
  stairBlocks,
  stairSystemOptionChoices,
  stairBlocksOptionChoices,
  rawOptionChoices,
  definitions,
  stairSettings,
  stairCatalog,
  mirror,
  mirrorTranslationX,
  resolvedDatums,
  columns,
  rooms,
  roomFloorAssemblies,
  entityDisabledByGroup,
  foundationPatternEdgeBehaviors,
  idToParents,
  relatedStairSystemsMap,
  enabledFeatures,
  isolatedEntity,
}: ReturnType<typeof select>): {
  stairSystems: Table<GeneratedStairSystem>;
  resolvedDatums: ResolvedDatumTable;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];
  const resultStairSystems: RWTable<GeneratedStairSystem> = {};

  if (!stairSystems) {
    return { stairSystems: resultStairSystems, issues: [], resolvedDatums };
  }
  const rawStairBlocks = stairBlocks;

  stairBlocks = evalFormulas(stairBlocks, (v) => stairBlocksOptionChoices[v], undefined, entityDisabledByGroup);
  stairSystems = evalFormulas(stairSystems, (v) => stairSystemOptionChoices[v], undefined, entityDisabledByGroup);

  const stairBlocksByParentId: { [stairSystemId: string]: StairBlock[] } = {};
  for (const id in stairBlocks) {
    const block = stairBlocks[id];

    // Defer archetype-disabled gate when modelComponents is enabled so that the per-instance
    // evaluation in generateRelatedStairSystemsMap can re-enable a block whose archetype
    // option resolves to disabled.
    if (block.disabled && !enabledFeatures.modelComponents) {
      continue;
    }

    const { stairSystemId } = block;
    if (stairBlocksByParentId[stairSystemId]) {
      stairBlocksByParentId[stairSystemId].push(block);
    } else {
      stairBlocksByParentId[stairSystemId] = [block];
    }
  }

  for (const id in stairSystems) {
    let stairSystem = stairSystems[id];

    if (stairSystem.disabled && !enabledFeatures.modelComponents) {
      continue;
    }

    if (!levels[stairSystem.levelId]) {
      issues.push({
        type: BuildingGenerationIssueType.STAIRWAY_MISSING_LEVEL,
        missingLevelId: stairSystem.levelId,
        handle: {
          type: "STAIR_SYSTEM",
          id: id,
        },
      });

      continue;
    }

    let stairBlocksByParent = (stairBlocksByParentId[id] ?? []).sort((a, b) => a.sortIndex - b.sortIndex);

    stairSystem = resolveStairSystem(stairSettings, stairSystem, undefined, stairCatalog, enabledFeatures);
    stairBlocksByParent = stairBlocksByParent.map((block) => resolveStairBlock(stairSystem, block));

    const { generated, issues: stairIssues } = generateStairSystem({
      stairSystem,
      stairBlocks: stairBlocksByParent,
      levels,
      mirror,
      mirrorTranslationX,
      resolvedDatums,
      rooms,
      roomFloorAssemblies,
      stairSystemOptionChoices,
      stairBlocksOptionChoices,
    });

    if (shouldGenerateEntity(id, idToParents, isolatedEntity) && !stairSystem.disabled) {
      // When modelComponents is on we keep archetype-disabled blocks in `generated.blocks`
      // so they can surface in component instances; strip them here for the archetype emit.
      if (enabledFeatures.modelComponents) {
        const archetypeBlocks: RWTable<GeneratedStairBlock> = {};
        for (const blockId in generated.blocks) {
          if (stairBlocks[blockId]?.disabled) {
            continue;
          }
          archetypeBlocks[blockId] = generated.blocks[blockId];
        }
        resultStairSystems[id] = { ...generated, blocks: archetypeBlocks };
      } else {
        resultStairSystems[id] = generated;
      }
    }

    issues.push(...stairIssues);

    if (enabledFeatures.modelComponents) {
      addStairSystemComponentInstances(
        id,
        generated,
        resultStairSystems,
        levels,
        mirror,
        mirrorTranslationX,
        rawStairBlocks,
        definitions,
        rawOptionChoices,
        relatedStairSystemsMap,
      );
    }
  }

  for (const id of Object.keys(resultStairSystems)) {
    const system = resultStairSystems[id];
    resultStairSystems[id] = aggregateWallTrimFacesFromBlocks(system);
    addPosts({
      resolvedDatums,
      columns,
      resultBlocks: resultStairSystems[id].blocks,
      issues,
      levelId: resultStairSystems[id].levelId,
      levels,
      foundationPatternEdgeBehaviors,
    });
  }

  return {
    stairSystems: resultStairSystems,
    resolvedDatums,
    issues,
  };
}

export default memo(select, generate, compare);
